import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import moment from 'moment';
import isEqual from 'lodash/isEqual';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { Button, setAutomationElement } from '@experian-uk/corvetteuk-common-ui';
import Analytics, { MilestoneInputEvent } from '@ecs-uk/web-common-analytics';
import getCurrentSubscriptionPrice from '../../reducers/selectors/currentSubscriptionPrice';
import {
  dateFormats,
  familyName,
  fulfillmentStatuses,
  products,
  serviceName,
  subscriptionActions,
} from '../../constants';
import Container from '../../components/Container';
import PanelContainer from '../../components/PanelContainer';
import PanelHeader from '../../components/PanelHeader';
import StyledSubscription from '../../components/StyledSubscription';
import TopContainer from '../../components/TopContainer';
import { getEnv } from '../../components/Context/env';
import { getHomeUrl, isObjectEmpty, getUpgradeType, getBreachBillingDatePrice } from '../../helpers';
import { isAnnualBillingFrequency } from '../../helpers/flex-helpers/flexBillingFrequency';
import { getFreeTrialData, mapFamilyToProduct } from '../../helpers/subscription';
import isServer from '../../helpers/isServer';
import isFlexSplitUser from '../../helpers/isFlexUser';
import subscriptionProps from '../../propTypes/subscription';
import messaging from './messaging';
import subtitle from './subtitle';
import getSubscriptionDetails from './getSubscriptionDetails';
import getCommonECDData from '../../reducers/selectors/getCommonECDData';
import { getAvailableOffers } from '../../reducers/offers';
import { commonEcdSchema } from '../../schemas';
import {
  Heading,
  CopyText,
  BodyContent,
  DetailsContainer,
  ButtonContainer,
  InlineLink,
  FreeButtonContainer,
  Reminder,
  ReminderList,
} from './index.styles';
import { trackUpsellStage } from '../../helpers/trackUpsell/trackUpsellStage';
import { PAYMENT_LOG_STEPS } from '../../constants/paymentLogSteps';
import isDebugMode from '../../helpers/isDebugMode';

dayjs.extend(advancedFormat);

const ThanksIndex = () => {
  const isFlexSplitEnabled = useSelector(state => isFlexSplitUser(state.split));
  const billing = useSelector(state => state.billing);
  const subscription = useSelector(state => state.subscriptions.current);
  const commonEcdData = useSelector(state => getCommonECDData(state));
  const currentSubscriptionPrice = useSelector(state => getCurrentSubscriptionPrice(state));
  const customer = useSelector(state => state.auth.currentUser);
  const confirmSelect = useSelector(state => state.confirmSelection);
  const cancellationReason = useSelector(state => state.cancellationReason.reason);
  const offers = useSelector(state => getAvailableOffers(state.offers, state.subscriptions));
  const selection = useSelector(state => state.selection || {});
  const upsellCorrelationId = useSelector(state => state.upsellProgress?.upsellCorrelationId);
  const hasCurrentSubscriptionPlanCode = Boolean(subscription?.planCode);
  const isFlexFlow = isFlexSplitEnabled || hasCurrentSubscriptionPlanCode;

  const location = useLocation();
  const history = useHistory();
  const paymentMethodInfo = location.state || {};
  const isFlexUserMigrationFailed = location.state?.flexUserMigrationFailed === true;

  const isEligibleForTrial = location.state?.isEligibleForTrial;
  const isValidRedirect = location.state?.isValidRedirect;
  const shouldShowFlexThanksMessage = isFlexFlow && !isFlexUserMigrationFailed;

  const [isECDInitialized, setIsECDInitialized] = useState(false);
  const [hasPublishedAnalytics, setHasPublishedAnalytics] = useState(false);

  const getNewProductSelection = () =>
    confirmSelect?.family ||
    (subscription?.defermentInfo ? subscription?.defermentInfo.family : mapFamilyToProduct(subscription?.family)) ||
    products.basic;

  useEffect(() => {
    const initializeAnalytics = async () => {
      if (!isServer() && commonEcdData && !isECDInitialized) {
        setIsECDInitialized(true);
        await Analytics.init(
          {
            ...commonEcdData,
            customer: {
              ...commonEcdData.customer,
              subscriptionType: confirmSelect?.oldProduct || commonEcdData.customer?.subscriptionType,
            },
            productChange: {
              switchingTo: getNewProductSelection(),
              currentPaymentMethod: paymentMethodInfo.paymentMethod ?? 'not_provided',
            },
          },
          { pageName: 'ProdMoveThanksPage', debugMode: isDebugMode(), serviceName }
        );
        trackUpsellStage(
          PAYMENT_LOG_STEPS.THANKS_PAGE_SHOWN,
          customer.customerId,
          upsellCorrelationId,
          selection.offerId,
          selection.family
        );
      }
    };

    initializeAnalytics();
  }, [commonEcdData]);

  const newProductSelection = getNewProductSelection();
  const oldProduct = confirmSelect ? confirmSelect.oldProduct : null;

  useEffect(() => {
    if (billing && subscription) {
      const pendingCancellation = subscription.defermentInfo?.type === subscriptionActions.cancellation;
      const isInTrial = subscription.freeTrial;
      const trialEndDate = subscription.freeTrialEndDt;
      let daysLeftInTrial;
      if (isInTrial && trialEndDate) {
        daysLeftInTrial = dayjs(trialEndDate).diff(dayjs(), 'day') + 1; // include today
      }

      const subscriptionFrom = oldProduct || subscription.family;
      const subscriptionTo = newProductSelection;
      const isDownsellToFree = subscriptionTo === products.basic;
      const pageLoadECD = {
        product_movement_success: {
          subscription_from: subscriptionFrom,
          subscription_to: subscriptionTo || 'unknown',
          is_eligible_for_trial: isEligibleForTrial ?? 'not_set',
          payment_method: paymentMethodInfo.paymentMethod ?? 'not_set',
          family: subscriptionFrom,
          productSubscriptionLookingToMoveTo: subscriptionTo,
          isWithinFreeTrial: isInTrial,
          isPendingCancellation: pendingCancellation,
          daysLeftOfTrialInCurrentProductSubscription: daysLeftInTrial,
          ...(isDownsellToFree && { cancellationType: 'deferred' }),
        },
      };

      const pageLoadEventAdded = window.$ECD2?.some(existingEcdEvent => isEqual(existingEcdEvent, pageLoadECD));
      if (
        !isServer() &&
        !hasPublishedAnalytics &&
        !pageLoadEventAdded &&
        isECDInitialized &&
        subscriptionFrom?.toLowerCase() !== subscriptionTo?.toLowerCase() &&
        isValidRedirect
      ) {
        Analytics.publish(MilestoneInputEvent.fromObject(pageLoadECD));
        setHasPublishedAnalytics(true);
        history.replace({
          pathname: location.pathname,
          state: {
            ...location.state,
            isValidRedirect: false,
          },
        });
      }
    }
  }, [
    isECDInitialized,
    hasPublishedAnalytics,
    billing,
    subscription,
    isValidRedirect,
    oldProduct,
    newProductSelection,
    isEligibleForTrial,
    history,
    location,
  ]);

  const renderReady = subscription && billing;

  if (!renderReady) {
    return null;
  }

  const env = getEnv();

  const { freeTrialTerms, nextBillingDate } = billing;

  const { customerNumber } = customer;

  const { thankYouMessage, subscriptionConfirmation } = messaging;

  const steps = [{ text: 'Subscriptions', url: '/' }];

  const breachSubscriptions = [products.breach, products.breachPlus];
  const isBreachUser = breachSubscriptions.includes(subscription.family);

  const { defermentInfo, freeTrialEndDt } = subscription;

  let { freeTrial } = confirmSelect || subscription;

  let identityDeferred = subscription.family === products.credit_expert && confirmSelect?.family === products.identity;

  if (!confirmSelect) {
    freeTrial &&= !isObjectEmpty(getFreeTrialData(freeTrialTerms, freeTrialEndDt));
    identityDeferred = defermentInfo && defermentInfo.family === products.identity;
  }

  const parseAndFormatPrice = price => (!Number.isNaN(parseInt(price, 10)) ? `£${price}` : '');
  const currentPrice = confirmSelect
    ? parseAndFormatPrice(confirmSelect.price)
    : parseAndFormatPrice(currentSubscriptionPrice);

  const cancellation = confirmSelect?.cancel;

  const headingStart = identityDeferred ? subscriptionConfirmation.deferred : subscriptionConfirmation.default;

  const currentOffer = offers.find(o => o.family === subscription?.breachDefermentInfo?.family);
  const newSelection = confirmSelect ? selection : { offerId: currentOffer?.id };
  const { deferredProductBillDate, deferredProductPrice: offerPrice } =
    getBreachBillingDatePrice(subscription, offers, newSelection) || {};
  const hasSelectedPrice =
    isBreachUser && confirmSelect?.price !== undefined && confirmSelect?.price !== null && confirmSelect?.price !== '';
  const deferredProductPrice = hasSelectedPrice ? confirmSelect.price : offerPrice;
  const isDeferredAnnual = isBreachUser && isAnnualBillingFrequency(confirmSelect?.billingFrequency);
  const hasDeferredBreachProduct = deferredProductBillDate && deferredProductPrice;
  const basicToBreach = !(currentPrice !== '£0' && currentPrice !== '£179.88');

  const subscriptionDetails = getSubscriptionDetails(
    nextBillingDate,
    currentPrice,
    deferredProductBillDate,
    hasDeferredBreachProduct,
    deferredProductPrice,
    isBreachUser,
    billing,
    basicToBreach,
    isDeferredAnnual
  );

  const buttonLabel =
    !identityDeferred && !hasDeferredBreachProduct
      ? `Go to your ${familyName[newProductSelection]} homepage`
      : 'Go to homepage';

  const inFreeTrial = freeTrial && !hasDeferredBreachProduct;
  const thanksRedirectUrl = getHomeUrl(subscription.family, inFreeTrial, isBreachUser);

  const paidSwitchButton = (
    <Button
      type="primary"
      label={buttonLabel}
      href={thanksRedirectUrl}
      onClick={() => {}}
      {...setAutomationElement('thanksButton')}
    />
  );

  const fulfillmentDataComplete =
    (!!nextBillingDate || isBreachUser) &&
    (shouldShowFlexThanksMessage ||
      subscription.fulfillmentStatus === fulfillmentStatuses.succeeded ||
      hasDeferredBreachProduct);

  const getHeading = () => {
    if (fulfillmentDataComplete || newProductSelection === products.basic || cancellation) {
      return <React.Fragment>Thank you</React.Fragment>;
    }
    return (
      <span>
        You are now subscribed to <StyledSubscription product={newProductSelection} />
      </span>
    );
  };

  const heading = getHeading();
  const moveToBreachPlus = subscription.family === products.breachPlus && oldProduct === products.breach;
  const moveToBreach = subscription.family === products.breach && oldProduct === products.breachPlus;

  return (
    <Container as="main" id="main">
      <TopContainer steps={steps} customerNumber={customerNumber} currentSubscription={subscription.family} />
      <Heading {...setAutomationElement('thanksTitle')}>{heading}</Heading>
      {newProductSelection !== products.basic && !cancellation && (
        <React.Fragment>
          <CopyText {...setAutomationElement('thanksDescription')}>
            {shouldShowFlexThanksMessage
              ? "Your account may take up to 24 hours to activate. If we cannot activate all your benefits, we'll give you a full refund within 5 working days."
              : thankYouMessage({
                  movementType: getUpgradeType(subscription.family, newProductSelection),
                  moveToBreachPlus,
                  moveToBreach,
                })[newProductSelection] || ''}
            {identityDeferred &&
              fulfillmentDataComplete &&
              ` Your subscription will change from ${familyName[subscription.family]} to ${
                familyName[products.identity]
              }${moment(nextBillingDate).isValid() ? ` on ${moment(nextBillingDate).format(dateFormats.long)}.` : '.'}`}
          </CopyText>
          {fulfillmentDataComplete && (
            <PanelContainer>
              <PanelHeader
                headingStart={headingStart}
                styledSubscription={newProductSelection}
                subtitle={subtitle(
                  currentPrice,
                  freeTrial,
                  isBreachUser,
                  deferredProductPrice,
                  basicToBreach,
                  isDeferredAnnual
                )}
                setAutomationTitle="thanksCardTitle"
                setAutomationSubtitle="thanksCardDescription"
              />
              <BodyContent>
                <DetailsContainer data={subscriptionDetails} />
                <ButtonContainer>{paidSwitchButton}</ButtonContainer>
              </BodyContent>
            </PanelContainer>
          )}
          {!fulfillmentDataComplete && paidSwitchButton}
        </React.Fragment>
      )}
      {(newProductSelection === products.basic || cancellation) && (
        <React.Fragment>
          <CopyText {...setAutomationElement('thanksDescription')}>
            You&rsquo;ll shortly have a free Experian account. Your account is still covered by the same{' '}
            <InlineLink href={`${env.REACT_APP_EXPERIAN_URL}/consumer/terms.html`}>Terms</InlineLink> and{' '}
            <InlineLink href={`${env.REACT_APP_EXPERIAN_URL}/consumer/privacy.html`}>Privacy Policy</InlineLink> you
            originally agreed to. If you previously paid for your subscription, we will not take any future payments.
          </CopyText>
          {cancellationReason === 'breach' && (
            <Reminder>
              Remember to activate your code once your billing period is over.
              <ReminderList>
                <li>
                  Once you’ve received your cancellation email, log in to your Experian account and go to ‘My
                  Subscriptions’.
                </li>
                <li>Select Identity Plus.</li>
                <li>Enter your voucher code for your complimentary Identity Plus subscription to begin.</li>
              </ReminderList>
            </Reminder>
          )}
          <FreeButtonContainer>
            <Button
              type="primary"
              label="Go to Experian homepage"
              href={getHomeUrl(subscription.family)}
              onClick={() => {}}
              {...setAutomationElement('thanksButton')}
            />
          </FreeButtonContainer>
        </React.Fragment>
      )}
    </Container>
  );
};

ThanksIndex.propTypes = {
  billing: PropTypes.shape({
    freeTrialTerms: PropTypes.shape({
      remaining: PropTypes.shape({
        value: PropTypes.number,
      }),
    }),
    nextBillingDate: PropTypes.string,
    endDate: PropTypes.string,
  }),
  commonEcdData: PropTypes.shape(commonEcdSchema),
  confirmSelect: PropTypes.shape({
    cancel: PropTypes.bool,
    family: PropTypes.string,
    freeTrial: PropTypes.bool,
    price: PropTypes.number,
    oldProduct: PropTypes.string,
  }),
  customer: PropTypes.shape({
    customerNumber: PropTypes.string,
  }),
  initializeEcd: PropTypes.func,
  subscription: subscriptionProps,
  currentSubscriptionPrice: PropTypes.number,
  cancellationReason: PropTypes.string,
  offers: PropTypes.arrayOf(PropTypes.shape({})),
  selection: PropTypes.shape({
    offerId: PropTypes.string,
    family: PropTypes.string,
  }),
};

ThanksIndex.defaultProps = {
  billing: {},
  commonEcdData: null,
  confirmSelect: null,
  customer: {},
  subscription: {},
  currentSubscriptionPrice: null,
  cancellationReason: undefined,
  initializeEcd: () => ({}),
  offers: [],
  selection: {},
};

export default ThanksIndex;
